import classNames from 'classnames'
import BSCard from 'react-bootstrap/Card'

import Spinner from '@/components/Spinner/Spinner'
import { CommandersPageProps } from '@/types/page'
import useDirectories from '@/utils/useDirectories'

import styles from './NavigationPanel.module.scss'
import NavigationPanelBracket from './NavigationPanelBracket'
import NavigationPanelBudget from './NavigationPanelBudget'
import NavigationPanelPartners from './NavigationPanelPartners'
import NavigationPanelTags from './NavigationPanelTags'
import NavigationPanelTop from './NavigationPanelTop'

type Props = {
  data: CommandersPageProps['data']
}

const getParams = ({ subsubpage, subsubsubpage, subsubsubsubpage }: ReturnType<typeof useDirectories>) => {
  const brackets = ['core', 'upgraded', 'optimized']
  const budgets = ['budget', 'expensive']
  const params: { bracket?: string; budget?: string; tag?: string } = {
    bracket: undefined,
    budget: undefined,
    tag: undefined,
  }

  if (!subsubpage) {
    return params
  } else if (brackets.includes(subsubpage)) {
    params.bracket = subsubpage
  } else if (budgets.includes(subsubpage)) {
    params.budget = subsubpage
  } else {
    params.tag = subsubpage
  }

  if (!subsubsubpage) {
    return params
  } else if (budgets.includes(subsubsubpage)) {
    params.budget = subsubsubpage
  } else {
    params.tag = subsubsubpage
  }

  if (!subsubsubsubpage) {
    return params
  } else {
    params.budget = subsubsubsubpage
  }

  return params
}

const NavigationPanel = ({ data }: Props) => {
  const directories = useDirectories()
  const card = data.container.json_dict.card

  if (!card) {
    return (
      <BSCard className={classNames(styles.navigation, 'justify-content-center shadow-sm')}>
        <Spinner className='fa-2x' />
      </BSCard>
    )
  }

  if (directories.dir === 'cards' && !card.legal_commander && !card.type.includes('Background')) {
    return null
  }

  const { bracket, budget, tag } = getParams(directories)

  const tagLinks = data.panels?.taglinks

  // Disable budget buttons if the currently selected commander+tag has <10 decks
  const numDecks = data.container.json_dict.card.num_decks
  const disableBudgetButtons = !budget && numDecks < 10

  return (
    <BSCard
      className={classNames(styles.navigation, 'shadow-sm', {
        'justify-content-start': tagLinks?.length === 0,
      })}
    >
      <NavigationPanelTop bracket={bracket} budget={budget} card={card} tag={tag} />

      {!['cards', 'deckpreview', 'partners'].includes(directories.dir) && (
        <>
          <NavigationPanelBracket bracket={bracket} budget={budget} sanitized_wo={card.sanitized_wo} tag={tag} />
          <NavigationPanelBudget
            bracket={bracket}
            budget={budget}
            disabled={disableBudgetButtons}
            sanitized_wo={card.sanitized_wo}
            tag={tag}
          />
        </>
      )}

      {tagLinks ? (
        <>
          <NavigationPanelTags
            bracket={bracket}
            budget={budget}
            sanitized_wo={card.sanitized_wo || ''}
            tag={tag}
            tags={tagLinks}
          />
        </>
      ) : (
        data.partnercounts && <NavigationPanelPartners partnercounts={data.partnercounts} />
      )}
    </BSCard>
  )
}

export default NavigationPanel
